/**
 * Reads/writes the last-used pipeline for the current tenant+user.
 *
 * `lastPipelineId` seeds the default pipeline on the New Deal surfaces;
 * `rememberPipeline` is called after a deal is created (centralised in the
 * useCreateDeal / useCreateDealFromLead mutations) so every create path
 * updates the remembered default.
 *
 * `lastPipelineId` is read straight from localStorage on every render rather
 * than held in state, on purpose: the write happens in a *different* hook
 * instance (inside the create mutation), so a local useState here would never
 * see it. A render-time read means each fresh render of a consumer (e.g. when
 * the New Deal dialog re-opens) picks up the latest value.
 */
import { useCallback } from 'react';
import { useAuth } from '../../../shared/auth';
import { getLastUsedPipelineId, setLastUsedPipelineId } from './lastUsedPipeline';

export function useLastUsedPipeline(): {
  lastPipelineId: string | null;
  rememberPipeline: (pipelineId: string) => void;
} {
  const { user } = useAuth();
  const tenantId = user?.tenant_id ?? 'unknown';
  const userId = user?.id ?? 'unknown';

  const lastPipelineId = getLastUsedPipelineId(tenantId, userId);

  const rememberPipeline = useCallback(
    (pipelineId: string) => setLastUsedPipelineId(tenantId, userId, pipelineId),
    [tenantId, userId],
  );

  return { lastPipelineId, rememberPipeline };
}
